import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Boxes, Car, Ship, Bike, Scissors, FileText, Sparkles } from "lucide-react";

const CATS = [
  { key: "auto", label: "Automobile", icon: Car },
  { key: "marine", label: "Nautisme", icon: Ship },
  { key: "moto", label: "Moto", icon: Bike },
];

export default function Gabarits() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("auto");
  const [catalog, setCatalog] = useState({ brands: [], elements: [] });
  const [brand, setBrand] = useState(null);
  const [model, setModel] = useState("");
  const [element, setElement] = useState("");
  const [gabarit, setGabarit] = useState(null);

  useEffect(() => {
    api.get(`/gabarits/catalog?category=${category}`).then((r) => {
      setCatalog(r.data);
      setBrand(null); setModel(""); setElement(""); setGabarit(null);
    });
  }, [category]);

  const generate = async () => {
    if (!brand || !model || !element) return toast.error("Sélectionnez marque, modèle et élément");
    const { data } = await api.post("/gabarits/generate", {
      category, brand: brand.brand, model, element,
    });
    setGabarit(data);
    toast.success("Gabarit généré");
  };

  const toNesting = () => {
    navigate("/imposition", { state: { pieces: gabarit.pieces } });
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center">
          <Boxes className="w-5 h-5 text-sky-400" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Gabarits & Addons</h1>
          <p className="text-slate-500">Générez un gabarit sur mesure à partir d'une base véhicules / bateaux / motos</p>
        </div>
      </div>

      <div className="flex gap-2">
        {CATS.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`cat-${c.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${category === c.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"}`}>
            <c.icon className="w-4 h-4" /> {c.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">1. Marque</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {catalog.brands.map((b) => (
                <button key={b.brand} onClick={() => { setBrand(b); setModel(""); }} data-testid={`brand-${b.brand}`}
                  className={`p-3 rounded-xl border bg-white flex flex-col items-center gap-2 transition-colors ${brand?.brand === b.brand ? "border-sky-400 ring-2 ring-sky-400/30" : "border-slate-200 hover:border-sky-300"}`}>
                  <div className="h-8 flex items-center">
                    <img src={b.logo} alt={b.brand} className="max-h-8 max-w-[70px] object-contain"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                  <span className="text-xs font-medium text-navy text-center leading-tight">{b.brand}</span>
                </button>
              ))}
            </div>
          </div>

          {brand && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">2. Modèle</h3>
              <div className="flex flex-wrap gap-2">
                {brand.models.map((m) => (
                  <button key={m} onClick={() => setModel(m)} data-testid={`model-${m}`}
                    className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${model === m ? "bg-sky-400 text-navy" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          {model && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">3. Élément à confectionner</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {catalog.elements.map((el) => (
                  <button key={el.element} onClick={() => setElement(el.element)} data-testid={`element-${el.element}`}
                    className={`text-left p-3 rounded-xl border bg-white transition-colors ${element === el.element ? "border-sky-400 ring-2 ring-sky-400/30" : "border-slate-200 hover:border-sky-300"}`}>
                    <div className="flex items-center gap-2 text-navy font-medium text-sm"><Scissors className="w-4 h-4 text-sky-500" /> {el.element}</div>
                    <div className="text-xs text-slate-500 mt-1 font-mono">{el.pieces.length} pièce(s)</div>
                  </button>
                ))}
              </div>
              <button onClick={generate} data-testid="generate-gabarit-btn"
                className="mt-4 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> Générer le gabarit
              </button>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          {gabarit ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="gabarit-result">
              <h3 className="font-display font-semibold text-navy text-lg">{gabarit.element}</h3>
              <p className="text-sm text-slate-500">{gabarit.brand} {gabarit.model}</p>
              <div className="mt-4 space-y-2">
                {gabarit.pieces.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                    <span className="text-sm font-medium text-navy">{p.name}</span>
                    <span className="text-xs font-mono text-slate-500">{p.width}×{p.height}cm ×{p.qty}</span>
                  </div>
                ))}
              </div>
              <button onClick={toNesting} data-testid="gabarit-to-nesting-btn"
                className="mt-4 w-full py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors inline-flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" /> Envoyer vers le plan d'imposition
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">
              <Boxes className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Sélectionnez une marque, un modèle et un élément pour générer un gabarit.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
